// Sugalarm — header Wi-Fi signal indicator (cycle 235.4).
//
// Renders 4 ascending signal bars showing Wi-Fi strength, beside the
// cycle-229 battery indicator. Unlike the battery (a passive readout), this
// IS a tap target by design: tapping flashes the exact `NN%` signal strength,
// which then fades away. It is NOT an alarm/ack control and is never
// paywalled — it only reveals a number.
//
// Colors are tokens only (mirrors the battery thresholds):
//   percent > 50  → --zone-ok     (green)
//   percent <= 50 → --zone-warn   (amber)
//   percent <= 25 → --zone-urgent (red)
// Filled bars use the strength color; the rest are --ink-3 (dim).
//
// Renders nothing when not connected or level < 0 (never a broken glyph).
// Only mounted by KioskHeader when `wifiSignal.isAvailable()` (the Android
// tablet kiosk APK), so it never appears on the Pi kiosk, iPad, web, or
// mobile.

import { useEffect, useRef, useState } from "react";

import styles from "./WifiIndicator.module.css";

interface Props {
  connected: boolean;
  /** 0–4 bars, or -1 when unknown/disconnected. */
  level: number;
  /** 0–100, or -1 when unknown/disconnected. */
  percent: number;
}

function strengthColor(percent: number): string {
  if (percent <= 25) return "var(--zone-urgent)";
  if (percent <= 50) return "var(--zone-warn)";
  return "var(--zone-ok)";
}

// 4 ascending bars: x positions + heights against the 20-tall baseline.
const BARS = [
  { x: 1, h: 6 },
  { x: 7, h: 10 },
  { x: 13, h: 14 },
  { x: 19, h: 18 },
] as const;

export function WifiIndicator({ connected, level, percent }: Props) {
  const [showPct, setShowPct] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  if (!connected || level < 0) return null;
  const bars = Math.max(0, Math.min(4, level));
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const color = strengthColor(pct);

  const reveal = (): void => {
    setShowPct(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    // Flash the % then fade away (the CSS handles the opacity transition).
    timerRef.current = window.setTimeout(() => setShowPct(false), 2200);
  };

  return (
    <button
      type="button"
      className={styles.wrap}
      onClick={reveal}
      aria-label={`Wi-Fi signal ${pct}%`}
      title={`Wi-Fi ${pct}%`}
    >
      <svg
        className={styles.glyph}
        viewBox="0 0 24 20"
        fill="none"
        aria-hidden="true"
      >
        {BARS.map((b, i) => (
          <rect
            key={b.x}
            x={b.x}
            y={20 - b.h}
            width="4"
            height={b.h}
            rx="1"
            fill={i < bars ? color : "var(--ink-3)"}
          />
        ))}
      </svg>
      <span
        className={`${styles.pct} tnum ${showPct ? styles.pctShown : ""}`}
        style={{ color }}
        aria-hidden="true"
      >
        {pct}%
      </span>
    </button>
  );
}
